import React, { useCallback, useContext, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { omit } from "ramda";
import { MediaContext } from "./media-context";

import Box from "@mui/material/Box";
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";

import EditColumn from "./EditColumn";
import Image from "../../components/Image";
import NewThumbnailDialog from "./NewThumbnailDialog";

const useStyles = makeStyles(theme => ({
  root: {
    paddingLeft: "0",
    width: "150px",
  },

  thumbnailOverlay: {
    height: "150px",
    width: "150px",
    position: "relative",
    borderRadius: "20px",
    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.15)",
    backgroundColor: "#E5E5E5",
  },

  thumbnail: {
    height: "150px",
    width: "150px",
    borderRadius: "20px",
    objectFit: "contain",
  },

  thumbnailEdit: {
    height: "36px",
    width: "36px",
    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.15)",
    borderRadius: "50px",
    backgroundColor: theme.palette.primary.main,
    position: "absolute",
    bottom: "10px",
    right: "10px",
    "&:hover": {
      backgroundColor: theme.palette.primary.mainHover,
    },
  },

  emptyThumbnail: {
    fontSize: "120px",
    color: theme.palette.primary.disabled,
    position: "absolute",
    right: "15px",
    top: "15px",
  },

  editIcon: {
    color: "white",
  },
}));

const NewThumbnailOverlay = props => {
  const classes = useStyles();
  const context = useContext(MediaContext);
  const { accept } = props;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  const onEditClick = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleCancel = useCallback(() => {
    context.setThumbnailUrl(null);
    setDialogOpen(false);
  }, [context]);

  const handleSave = useCallback(
    (url, file) => {
      setUploading(true);
      setDialogOpen(false);
      context.setThumbnailUrl(url);
      context.setThumbnailFile(file);
      setUploading(false);
      if (context.setErrors) {
        context.setErrors(omit(["thumbnail"]));
      }
    },
    [context],
  );

  const formBuilder = useCallback(
    handlers => {
      return (
        <Box className={classes.thumbnailOverlay}>
          <Image
            alt={context.media.name}
            src={
              context.thumbnailUrl || `${context.thumbnailUrl}&tc${Date.now()}`
            }
            className={classes.thumbnail}
          />
          <IconButton
            classes={{ root: classes.thumbnailEdit }}
            onClick={onEditClick}
            size="large"
          >
            <EditIcon fontSize="large" classes={{ root: classes.editIcon }} />
          </IconButton>
          <NewThumbnailDialog
            visible={dialogOpen}
            uploading={uploading}
            media={context.media}
            accept={accept}
            onCancel={handleCancel}
            onSave={handleSave}
          />
        </Box>
      );
    },
    [
      accept,
      context,
      classes,
      dialogOpen,
      uploading,
      handleSave,
      handleCancel,
      onEditClick,
    ],
  );

  return <EditColumn className={classes.root} formBuilder={formBuilder} />;
};

export default NewThumbnailOverlay;
